import React, { useRef, useEffect, useState } from 'react';
import { VideoContent } from '../../types/content';
import { useVideoAnalytics } from '../../hooks/useVideoAnalytics';

interface ShortsPlayerProps {
  video: VideoContent;
  className?: string;
  onPlayStateChange?: (isPlaying: boolean) => void;
  autoPlay?: boolean;
}

export const ShortsPlayer: React.FC<ShortsPlayerProps> = ({
  video,
  className = '',
  onPlayStateChange,
  autoPlay = true,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(video.duration || 0);

  // Initialize video analytics
  useVideoAnalytics({
    videoId: video.id,
    duration: duration,
    isPlaying,
    currentTime,
  });

  // Add duration and timeupdate listeners
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('durationchange', handleDurationChange);
    
    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('durationchange', handleDurationChange);
    };
  }, []);

  // For YouTube videos, handle duration through iframe messages
  useEffect(() => {
    if (!video.youtube || !autoPlay) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data && typeof event.data === 'object') {
        // Handle YouTube player events
        if (event.data.info && event.data.info.duration) {
          setDuration(event.data.info.duration);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [video.youtube, autoPlay]);

  // Reset state when autoPlay changes (video visibility changes)
  useEffect(() => {
    if (!autoPlay) {
      setIsPlaying(false);
      setIsMuted(true);
      onPlayStateChange?.(false);
    } else {
      setIsPlaying(true);
      onPlayStateChange?.(true);
    }
  }, [autoPlay, onPlayStateChange]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Only handle clicks if this is the current video
    if (!autoPlay) return;

    if (!video.url && video.youtube) {
      // For YouTube videos, we manage play state ourselves
      const newPlayState = !isPlaying;
      setIsPlaying(newPlayState);
      onPlayStateChange?.(newPlayState);
      if (isMuted) setIsMuted(false);
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      if (videoElement.paused) {
        videoElement.play().then(() => {
          setIsPlaying(true);
          onPlayStateChange?.(true);
        }).catch(console.error);
      } else {
        videoElement.pause();
        setIsPlaying(false);
        onPlayStateChange?.(false);
      }
    }
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (autoPlay) {
      videoElement.play().catch(console.error);
    } else {
      videoElement.pause();
    }

    return () => {
      videoElement.pause();
    };
  }, [video.id, autoPlay]);

  // If no URL but has YouTube ID, use YouTube embed
  if (!video.url && video.youtube) {
    return (
      <div className="relative w-full h-full" onClick={handleClick}>
        {/* Show thumbnail while loading or not current video */}
        {(video.thumbnail && (isLoading || !autoPlay)) && (
          <img 
            src={video.thumbnail} 
            alt={video.title} 
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {autoPlay && (
          <iframe
            key={`${video.youtube}-${isPlaying}`} // Force iframe refresh when play state changes
            src={`https://www.youtube.com/embed/${video.youtube}?autoplay=${isPlaying ? '1' : '0'}&controls=0&rel=0&showinfo=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&loop=1&playlist=${video.youtube}${isMuted ? '&mute=1' : ''}&origin=${window.location.origin}&enablejsapi=0&playsinline=1&color=white`}
            className={`w-full h-full object-cover ${className}`}
            frameBorder="0"
            allowFullScreen
            allow="autoplay"
            onLoad={() => {
              setIsLoading(false);
              if (autoPlay) {
                setIsMuted(false);
              }
            }}
          />
        )}
        {/* Show play button on thumbnail or when paused */}
        {(!isPlaying || isLoading || !autoPlay) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              <svg 
                className="w-12 h-12 text-white" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Otherwise use regular video player
  return (
    <div className="relative w-full h-full" onClick={handleClick}>
      <video
        ref={videoRef}
        src={video.url}
        className={`w-full h-full object-cover ${className}`}
        playsInline
        loop
        controls={false}
        poster={video.thumbnail}
      />
      
      {(!isPlaying || !autoPlay) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="w-20 h-20 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <svg 
              className="w-12 h-12 text-white" 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}; 